import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { CircleCheck, CircleAlert, Copy, Info, X } from 'lucide-react'
import type { QueryState } from '../lib/hooks'
import { initials } from '../lib/format'
import { EmptyState, ErrorBox, Loading } from './EmptyState'

// ---------------------------------------------------------------------------
// Async: loading / error / empty / data in one place
// ---------------------------------------------------------------------------
export function Async<T>({ q, children, empty, isEmpty, loading, className }: {
  q: QueryState<T>
  children: (data: T) => ReactNode
  empty?: ReactNode
  isEmpty?: (d: T) => boolean
  loading?: ReactNode
  className?: string
}) {
  if (q.error && q.data === undefined) return <ErrorBox error={q.error} retry={() => q.reload()} className={className} />
  if (q.data === undefined) return <>{loading ?? <Loading className={className} />}</>
  const emptyCheck = isEmpty ?? ((d: T) => Array.isArray(d) && d.length === 0)
  if (empty && emptyCheck(q.data)) return <>{empty}</>
  return <>{children(q.data)}</>
}

export { EmptyState }

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------
const AV_COLORS = [['#fed7aa', '#9a3412'], ['#c7d2fe', '#312e81'], ['#bbf7d0', '#14532d'], ['#fbcfe8', '#831843'], ['#bae6fd', '#0c4a6e'], ['#fde68a', '#78350f'], ['#ddd6fe', '#4c1d95']]
export function Avatar({ name, size = 32, square = false, color }: { name: string | null | undefined; size?: number; square?: boolean; color?: string }) {
  const n = name ?? '?'
  let h = 0
  for (const ch of n) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  const [bg, fg] = AV_COLORS[h % AV_COLORS.length]
  return (
    <div
      className="flex items-center justify-center font-bold shrink-0"
      style={{ width: size, height: size, borderRadius: square ? Math.round(size * 0.28) : '50%', background: color ?? bg, color: color ? '#fff' : fg, fontSize: Math.max(9, Math.round(size * 0.36)) }}
    >
      {initials(n)}
    </div>
  )
}

export function TenantLogo({ letter, color, size = 20, url }: { letter: string; color: string; size?: number; url?: string | null }) {
  const [broken, setBroken] = useState(false)
  if (url && !broken) return <img src={url} alt="" onError={() => setBroken(true)} className="shrink-0 object-contain bg-white" style={{ width: size, height: size, borderRadius: Math.round(size / 4) }} />
  return (
    <div className="flex items-center justify-center text-white font-extrabold shrink-0" style={{ width: size, height: size, borderRadius: Math.round(size / 4), background: color, fontSize: Math.round(size * 0.45) }}>
      {letter}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Definition tooltip (pure CSS hover, keyboard focusable)
// ---------------------------------------------------------------------------
export function Tip({ text, children, className = '' }: { text: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <span className={`relative inline-flex items-center group/tip ${className}`} tabIndex={0}>
      {children ?? <Info size={13} className="text-faint" />}
      <span role="tooltip" className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 hidden group-hover/tip:block group-focus/tip:block w-64 rounded-lg bg-slate-900 text-white text-[11.5px] font-normal leading-snug px-3 py-2 shadow-lg normal-case tracking-normal text-left">
        {text}
      </span>
    </span>
  )
}

// ---------------------------------------------------------------------------
// Tabs (controlled)
// ---------------------------------------------------------------------------
export function Tabs<K extends string>({ tabs, value, onChange, className = '' }: { tabs: { key: K; label: ReactNode; count?: number | null }[]; value: K; onChange: (k: K) => void; className?: string }) {
  return (
    <div className={`flex gap-1 flex-wrap ${className}`} role="tablist">
      {tabs.map((t) => (
        <button key={t.key} role="tab" aria-selected={value === t.key} className={`tab ${value === t.key ? 'on' : ''}`} onClick={() => onChange(t.key)}>
          {t.label}{t.count !== undefined && t.count !== null && <b className="ml-1 font-bold">{t.count}</b>}
        </button>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Drawer and modal
// ---------------------------------------------------------------------------
export function Drawer({ open, onClose, title, children, width = 560, footer }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; width?: number; footer?: ReactNode }) {
  useEffect(() => {
    if (!open) return
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [open, onClose])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <div className="relative h-full bg-white shadow-2xl flex flex-col" style={{ width: `min(${width}px, 100vw)` }}>
        <div className="h-14 shrink-0 border-b border-line flex items-center px-5 gap-3">
          <div className="font-bold text-[15px] flex-1 min-w-0 truncate">{title}</div>
          <button className="btn ghost sm" onClick={onClose} aria-label="Close"><X /></button>
        </div>
        <div className="flex-1 overflow-y-auto scroll-thin">{children}</div>
        {footer && <div className="shrink-0 border-t border-line px-5 py-3">{footer}</div>}
      </div>
    </div>
  )
}

export function Modal({ open, onClose, title, children, footer, width = 480 }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode; width?: number }) {
  useEffect(() => {
    if (!open) return
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [open, onClose])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/35" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full flex flex-col max-h-[90vh]" style={{ maxWidth: width }}>
        <div className="flex items-center px-5 pt-4 pb-2 gap-3">
          <div className="font-bold text-[15px] flex-1">{title}</div>
          <button className="btn ghost sm" onClick={onClose} aria-label="Close"><X /></button>
        </div>
        <div className="px-5 pb-4 overflow-y-auto">{children}</div>
        {footer && <div className="px-5 py-3 border-t border-line flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Form bits
// ---------------------------------------------------------------------------
export function Toggle({ on, onChange, disabled, label }: { on: boolean; onChange?: (v: boolean) => void; disabled?: boolean; label?: string }) {
  return (
    <button
      type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled}
      onClick={() => onChange?.(!on)}
      className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${on ? 'bg-brand' : 'bg-slate-300'} ${disabled ? 'opacity-50' : ''}`}
    >
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${on ? 'left-[18px]' : 'left-0.5'}`} />
    </button>
  )
}

export function Field({ label, hint, children, className = '' }: { label: ReactNode; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="label">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-faint mt-1">{hint}</span>}
    </label>
  )
}

export function Segmented<K extends string>({ options, value, onChange, size = 'md' }: { options: { key: K; label: ReactNode }[]; value: K; onChange: (k: K) => void; size?: 'sm' | 'md' }) {
  return (
    <div className="inline-flex rounded-lg border border-line bg-bg p-0.5">
      {options.map((o) => (
        <button key={o.key} type="button" onClick={() => onChange(o.key)}
          className={`rounded-md font-semibold ${size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-3 py-1 text-[12px]'} ${value === o.key ? 'bg-white shadow-sm text-brand' : 'text-muted hover:text-ink'}`}>
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function CopyBox({ text, className = '' }: { text: string; className?: string }) {
  const toast = useToast()
  return (
    <div className={`flex items-center gap-2 rounded-lg bg-slate-900 text-slate-100 px-3 py-2 ${className}`}>
      <code className="code flex-1 min-w-0 [overflow-wrap:anywhere]">{text}</code>
      <button className="btn sm" onClick={() => { void navigator.clipboard?.writeText(text); toast('Copied') }}><Copy /> Copy</button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------
type ToastKind = 'ok' | 'error' | 'info'
interface ToastItem { id: number; text: string; kind: ToastKind }
const ToastCtx = createContext<(text: string, kind?: ToastKind) => void>(() => {})

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const push = useCallback((text: string, kind: ToastKind = 'ok') => {
    const id = Date.now() + Math.random()
    setItems((x) => [...x.slice(-3), { id, text, kind }])
    setTimeout(() => setItems((x) => x.filter((i) => i.id !== id)), kind === 'error' ? 6000 : 3000)
  }, [])
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] flex flex-col gap-2 items-center pointer-events-none">
        {items.map((t) => (
          <div key={t.id} className={`pointer-events-auto flex items-center gap-2 rounded-xl px-4 py-2.5 shadow-xl text-[12.5px] font-medium ${t.kind === 'error' ? 'bg-red-600 text-white' : 'bg-slate-900 text-white'}`}>
            {t.kind === 'error' ? <CircleAlert size={15} /> : t.kind === 'ok' ? <CircleCheck size={15} className="text-emerald-400" /> : <Info size={15} />}
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  )
}
export const useToast = () => useContext(ToastCtx)

// ---------------------------------------------------------------------------
// Little layout helpers
// ---------------------------------------------------------------------------
export function Stat({ label, value, sub, tip, highlight }: { label: ReactNode; value: ReactNode; sub?: ReactNode; tip?: ReactNode; highlight?: boolean }) {
  return (
    <div className={`card px-3.5 py-3 min-w-0 ${highlight ? 'border-amber-200 bg-[#fffdf5]' : ''}`}>
      <div className="text-[11.5px] text-muted font-semibold flex items-center gap-1.5">{label}{tip && <Tip text={tip} />}</div>
      <div className="text-[26px] font-extrabold tracking-tight mt-1 leading-tight">{value}</div>
      {sub && <div className="text-[11px] mt-0.5 text-muted">{sub}</div>}
    </div>
  )
}

export function Meter({ value, color = '#4338ca', className = '' }: { value: number; color?: string; className?: string }) {
  return <div className={`bar ${className}`}><i style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%`, background: color }} /></div>
}
